import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { ORIGEM_WHATSAPP, ORIGEM_WHATSAPP_LABELS } from '@/pages/painel/lib/inteligencia';
import {
  parseCSV, parseXLSX, autoMapear, rowToCanonical, hashTexto,
} from '@/pages/painel/lib/whatsappImport';
import ColunaMapper from '@/pages/painel/components/importacao/ColunaMapper';
import ImportacaoRelatorio from '@/pages/painel/components/importacao/ImportacaoRelatorio';
import CorrespondenciaPendente from '@/pages/painel/components/importacao/CorrespondenciaPendente';

interface Batch {
  id: number;
  arquivo_nome: string | null;
  status: string;
  total_linhas: number;
  total_importadas: number;
  total_duplicadas: number;
  total_erros: number;
  iniciado_em: string | null;
  concluido_em: string | null;
}

type Passo = 'upload' | 'mapear' | 'processando' | 'relatorio';

const CHUNK_STAGE = 300;
const CHUNK_PROCESS = 400;

export default function ImportacaoWhatsApp({ password }: { password: string }) {
  const [passo, setPasso] = useState<Passo>('upload');
  const [origem, setOrigem] = useState<'whatsapp_normal' | 'whatsapp_business'>('whatsapp_normal');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheetAtual, setSheetAtual] = useState('');
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [hash, setHash] = useState('');

  const [batch, setBatch] = useState<Batch | null>(null);
  const [resumo, setResumo] = useState<any>(null);
  const [regras, setRegras] = useState<any>(null);
  const [detalheRegras, setDetalheRegras] = useState<any[] | null>(null);

  const [progresso, setProgresso] = useState({ enviadas: 0, processadas: 0 });
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aplicandoRegras, setAplicandoRegras] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadBatches = async () => {
    try {
      const { data } = await supabase.functions.invoke('inteligencia-admin', { body: { action: 'batches_listar', password } });
      const parsed = data as { batches?: Batch[] };
      setBatches(parsed?.batches ?? []);
    } catch { /* silencioso */ }
  };

  useEffect(() => { loadBatches(); /* eslint-disable-next-line */ }, [password]);

  const aplicarArquivo = (nome: string, hs: string, mapa: Record<string, string>, hdrs: string[], rws: Record<string, string>[]) => {
    setFileName(nome);
    setHash(hs);
    setMapping(mapa);
    setHeaders(hdrs);
    setRows(rws);
    setPasso('mapear');
    setError(null);
  };

  const handleFile = async (file: File) => {
    setError(null);
    try {
      const nome = file.name.toLowerCase();
      if (nome.endsWith('.xlsx') || nome.endsWith('.xls')) {
        const buf = await file.arrayBuffer();
        setBuffer(buf);
        const { sheets: sh, headers: h, rows: r } = parseXLSX(buf);
        if (!h.length) throw new Error('Não foi possível ler a planilha.');
        setSheets(sh);
        setSheetAtual(sh[0] ?? '');
        aplicarArquivo(file.name, hashTexto(`${file.name}-${file.size}`), autoMapear(h), h, r);
      } else {
        const texto = await file.text();
        const { headers: h, rows: r } = parseCSV(texto);
        if (!h.length) throw new Error('Não foi possível ler o arquivo CSV.');
        setSheets([]);
        setBuffer(null);
        aplicarArquivo(file.name, hashTexto(texto), autoMapear(h), h, r);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao ler o arquivo.');
    }
  };

  const trocarSheet = (aba: string) => {
    if (!buffer) return;
    setSheetAtual(aba);
    const { headers: h, rows: r } = parseXLSX(buffer, aba);
    setHeaders(h);
    setRows(r);
    setMapping(autoMapear(h));
  };

  const iniciarImportacao = async () => {
    if (!mapping.nome) { setError('Mapeie ao menos o campo "Nome / Contato".'); return; }
    if (!mapping.source_record_id) { setError('Mapeie o "ID do registro (ContatoID)" para garantir importação idempotente.'); return; }
    setLoading(true);
    setError(null);
    setPasso('processando');
    setProgresso({ enviadas: 0, processadas: 0 });

    try {
      const { data: bData, error: bErr } = await supabase.functions.invoke('whatsapp-import', {
        body: { action: 'batch_iniciar', password, arquivo_nome: fileName, origem_whatsapp: origem, total_linhas: rows.length, hash_arquivo: hash },
      });
      if (bErr) throw new Error(await extractErrorMessage(bErr));
      const newBatch = (bData as any)?.batch as Batch;
      if (!newBatch?.id) throw new Error((bData as any)?.error ?? 'Falha ao criar lote.');
      setBatch(newBatch);

      const fonte = origem === 'whatsapp_business' ? 'wpp_business' : 'wpp_normal';

      // 1) Envia em blocos para o staging
      for (let i = 0; i < rows.length; i += CHUNK_STAGE) {
        const fatia = rows.slice(i, i + CHUNK_STAGE);
        const registros = fatia.map((r, idx) => rowToCanonical(r, mapping, origem, i + idx));
        const { error: sErr } = await supabase.functions.invoke('whatsapp-import', {
          body: { action: 'stage_lote', password, batch_id: newBatch.id, fonte_arquivo: fonte, registros },
        });
        if (sErr) throw new Error(await extractErrorMessage(sErr));
        setProgresso((p) => ({ ...p, enviadas: Math.min(i + CHUNK_STAGE, rows.length) }));
      }

      // 2) Processa em blocos até concluir
      let processadas = 0;
      for (let guard = 0; guard < 200; guard += 1) {
        const { data: pData, error: pErr } = await supabase.functions.invoke('whatsapp-import', {
          body: { action: 'processar', password, batch_id: newBatch.id, limite: CHUNK_PROCESS },
        });
        if (pErr) throw new Error(await extractErrorMessage(pErr));
        const parsed = pData as any;
        if (parsed?.error) throw new Error(parsed.error);
        processadas += parsed?.processados ?? 0;
        setProgresso((p) => ({ ...p, processadas }));
        if (parsed?.concluido) break;
      }

      // 3) Relatório
      const { data: rData } = await supabase.functions.invoke('whatsapp-import', {
        body: { action: 'relatorio', password, batch_id: newBatch.id },
      });
      const rel = rData as any;
      setBatch(rel?.batch ?? newBatch);
      setResumo(rel?.resumo ?? null);
      setRegras(rel?.regras ?? null);
      setPasso('relatorio');
      await loadBatches();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro na importação.');
      setPasso('mapear');
    } finally {
      setLoading(false);
    }
  };

  const aplicarRegras = async () => {
    setAplicandoRegras(true);
    setError(null);
    try {
      const { data, error: e } = await supabase.functions.invoke('whatsapp-import', { body: { action: 'aplicar_regras', password } });
      if (e) throw new Error(await extractErrorMessage(e));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);
      setDetalheRegras(parsed?.detalhe ?? []);
      setRegras({ total: parsed?.total, aplicadas: parsed?.aplicadas, aguardando: parsed?.pendentes, ignoradas: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao aplicar regras.');
    } finally {
      setAplicandoRegras(false);
    }
  };

  const reiniciar = () => {
    setPasso('upload');
    setRows([]); setHeaders([]); setMapping({}); setSheets([]); setBuffer(null);
    setBatch(null); setResumo(null); setRegras(null); setDetalheRegras(null);
    setFileName(''); setHash('');
    setError(null);
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <i className="ri-whatsapp-line text-[#E8610A]"></i>
            <h3 className="text-sm font-bold text-gray-900">Importação de WhatsApp</h3>
          </div>
          {passo !== 'upload' && (
            <button onClick={reiniciar} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap">
              <i className="ri-restart-line"></i> Nova importação
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-5">
          Carregue o arquivo consolidado dos WhatsApps (CSV ou XLSX). Os dados vão para staging, são validados, normalizados e casados com o CRM antes de qualquer gravação — sem duplicar contatos.
        </p>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>}

        {passo === 'upload' && (
          <>
            <div className="flex items-center gap-1.5 flex-wrap mb-4">
              <span className="text-xs font-semibold text-gray-500 mr-1">Origem do arquivo:</span>
              {ORIGEM_WHATSAPP.map((o) => {
                const ativo = origem === o.key;
                return (
                  <button key={o.key} onClick={() => setOrigem(o.key as any)} className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${ativo ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]'}`}>
                    {ativo && <i className="ri-check-line"></i>}
                    {ORIGEM_WHATSAPP_LABELS[o.key]}
                  </button>
                );
              })}
            </div>

            <div onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center cursor-pointer hover:border-[#E8610A] transition-colors">
              <div className="w-12 h-12 mx-auto flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl mb-3">
                <i className="ri-upload-cloud-2-line text-2xl"></i>
              </div>
              <p className="text-sm font-semibold text-gray-700">Clique para selecionar o arquivo</p>
              <p className="text-xs text-gray-400 mt-1">CSV, TXT ou planilha XLSX</p>
              <input ref={inputRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
            </div>
          </>
        )}

        {passo === 'mapear' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg">
                <i className="ri-file-text-line text-gray-500"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">{rows.length.toLocaleString('pt-BR')} linhas · {headers.length} colunas · origem {ORIGEM_WHATSAPP_LABELS[origem]}</p>
              </div>
              {sheets.length > 1 && (
                <select value={sheetAtual} onChange={(e) => trocarSheet(e.target.value)} className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white cursor-pointer">
                  {sheets.map((s) => <option key={s} value={s}>Aba: {s}</option>)}
                </select>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <i className="ri-information-line text-[#E8610A]"></i>
              As colunas foram mapeadas automaticamente. Confira e ajuste o que precisar.
            </div>

            <ColunaMapper headers={headers} mapping={mapping} onChange={(k, v) => setMapping((m) => ({ ...m, [k]: v }))} />

            <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
              <p className="text-xs text-gray-400">Contatos sem empresa identificável irão para a fila de correspondência (nada fictício é criado).</p>
              <button onClick={iniciarImportacao} disabled={loading} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-5 py-2.5 cursor-pointer whitespace-nowrap disabled:opacity-60">
                <i className="ri-play-circle-line"></i> Iniciar importação
              </button>
            </div>
          </div>
        )}

        {passo === 'processando' && (
          <div className="py-8 text-center">
            <div className="w-12 h-12 mx-auto flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl mb-4">
              <i className="ri-loader-4-line text-2xl animate-spin"></i>
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-1">Importando…</p>
            <p className="text-xs text-gray-500 mb-4">{progresso.enviadas.toLocaleString('pt-BR')} / {rows.length.toLocaleString('pt-BR')} linhas processadas</p>
            <div className="max-w-md mx-auto h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#E8610A] rounded-full transition-all" style={{ width: `${rows.length ? Math.min(100, (progresso.processadas / rows.length) * 100) : 0}%` }} />
            </div>
          </div>
        )}

        {passo === 'relatorio' && (
          <ImportacaoRelatorio
            batch={batch}
            resumo={resumo}
            regras={regras}
            detalheRegras={detalheRegras}
            onAplicarRegras={aplicarRegras}
            aplicandoRegras={aplicandoRegras}
          />
        )}
      </section>

      <CorrespondenciaPendente password={password} />

      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Lotes de importação</p>
        {batches.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhuma importação registrada ainda.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{b.arquivo_nome || `Lote #${b.id}`}</p>
                  <p className="text-xs text-gray-500">{b.total_importadas.toLocaleString('pt-BR')} importadas · {b.total_duplicadas} duplicadas · {b.total_erros} erros</p>
                </div>
                <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 whitespace-nowrap ${b.status === 'concluido' ? 'bg-green-50 text-green-700' : b.status === 'erro' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>{b.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}